import { useEffect, useRef, useState } from "react";
import { Hotel, Landmark, Loader2, MapPin, Navigation, Plane, TrainFront } from "lucide-react";
import { toast } from "sonner";

const BAN = "https://data.geopf.fr/geocodage";
const PHOTON = "https://photon.komoot.io/api/";
const BIAS = { lat: 43.5528, lon: 7.0176 }; // Cannes — biais de classement (pas un filtre)

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// places : lieux connus (aéroports, gares…) passés par le parent — affichés instantanément en tête
export const AddressInput = ({ value, onChange, onSelect, placeholder, testid, className, lang = "fr", places = [] }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const abort = useRef(null);
  const wrap = useRef(null);

  const matchPlaces = (q) => {
    const tokens = norm(q).split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return places
      .filter((p) => tokens.every((tok) => norm(`${p.label} ${p.sublabel || ""}`).includes(tok)))
      .slice(0, 3)
      .map((p) => ({
        key: `place-${p.id}`,
        label: p.label,
        sub: p.kind === "airport" ? (lang === "en" ? "Airport" : "Aéroport")
          : p.kind === "station" ? (lang === "en" ? "Train station" : "Gare") : (p.sublabel || ""),
        icon: p.kind === "airport" ? "plane" : p.kind === "station" ? "train" : "pin",
        lat: p.lat, lon: p.lng,
        exact: p.kind === "airport" || p.kind === "station",
      }));
  };

  const photonLabel = (p) => {
    const addr = [p.housenumber, p.street].filter(Boolean).join(" ");
    const town = [p.postcode, p.city].filter(Boolean).join(" ");
    return [p.name, addr, town].filter(Boolean).join(", ");
  };

  useEffect(() => {
    const q = value.trim();
    if (q.length < 3) { setItems([]); return undefined; }
    // 1. Lieux connus : réponse INSTANTANÉE (pas d'attente réseau)
    const local = matchPlaces(q);
    if (local.length) { setItems(local); setOpen(true); }
    // 2. Adresses BAN + hôtels/lieux (Photon) en parallèle, fusionnées
    const timer = setTimeout(async () => {
      abort.current?.abort();
      abort.current = new AbortController();
      setBusy(true);
      try {
        const banParams = new URLSearchParams({
          index: "address", q, autocomplete: "true",
          lat: String(BIAS.lat), lon: String(BIAS.lon), limit: "4",
        });
        const photonParams = new URLSearchParams({
          q, lat: String(BIAS.lat), lon: String(BIAS.lon), limit: "4", lang,
        });
        const [banRes, photonRes] = await Promise.all([
          fetch(`${BAN}/search?${banParams}`, { signal: abort.current.signal }),
          fetch(`${PHOTON}?${photonParams}`, { signal: abort.current.signal }),
        ]);
        const banData = banRes.ok ? await banRes.json() : { features: [] };
        const photonData = photonRes.ok ? await photonRes.json() : { features: [] };

        const ban = (banData.features || []).map((f) => ({
          key: `ban-${f.properties.id || f.properties.label}`,
          label: f.properties.label,
          sub: f.properties.city || "",
          icon: "pin",
          lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0],
          exact: f.properties.type === "housenumber",
        }));
        const poi = (photonData.features || [])
          .filter((f) => f.properties.name)
          .map((f) => {
            const p = f.properties;
            return {
              key: `poi-${p.osm_type || ""}${p.osm_id || p.name}`,
              label: photonLabel(p),
              sub: [p.city, p.osm_value === "hotel" ? (lang === "en" ? "Hotel" : "Hôtel") : ""].filter(Boolean).join(" · "),
              icon: p.osm_value === "hotel" ? "hotel" : "landmark",
              lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0],
              exact: true, // lieu nommé (hôtel, plage, palais…) = destination connue du chauffeur
            };
          });

        // Fusion + déduplication par libellé normalisé
        const seen = new Set();
        const merged = [...local, ...poi, ...ban].filter((it) => {
          const k = norm(it.label);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }).slice(0, 7);

        setItems(merged);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setItems(local);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choose = (item) => {
    onChange(item.label);
    onSelect?.({ label: item.label, city: item.sub, lat: item.lat, lon: item.lon, exact: item.exact });
    setItems([]);
    setOpen(false);
  };

  const locate = () => {
    if (!navigator.geolocation) {
      return toast.error(lang === "en" ? "Geolocation is not supported by this browser." : "La géolocalisation n'est pas supportée par ce navigateur.");
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          const params = new URLSearchParams({
            index: "address", lat: String(p.coords.latitude), lon: String(p.coords.longitude),
            limit: "1", type: "housenumber",
          });
          const r = await fetch(`${BAN}/reverse?${params}`);
          const data = await r.json();
          const f = data.features?.[0];
          if (f) {
            onChange(f.properties.label);
            onSelect?.({
              label: f.properties.label, city: f.properties.city || "",
              lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0],
              exact: true,
            });
            setItems([]);
            setOpen(false);
          } else {
            toast.error(lang === "en" ? "No address found near your position." : "Aucune adresse trouvée près de votre position.");
          }
        } catch {
          toast.error(lang === "en" ? "Location unavailable." : "Position indisponible pour le moment.");
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        toast.error(lang === "en" ? "GPS permission denied or position unavailable." : "Autorisation GPS refusée ou position indisponible.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
    return undefined;
  };

  const iconFor = (icon) =>
    icon === "plane" ? <Plane size={15} className="shrink-0 text-azure" />
      : icon === "train" ? <TrainFront size={15} className="shrink-0 text-azure" />
      : icon === "hotel" ? <Hotel size={15} className="shrink-0 text-gold" />
      : icon === "landmark" ? <Landmark size={15} className="shrink-0 text-gold" />
      : <MapPin size={15} className="shrink-0 text-navy/35" />;

  return (
    <div ref={wrap} className="relative" data-testid={`${testid}-wrap`}>
      <div className="relative">
        <input
          data-testid={testid}
          className={`${className} pr-11`}
          placeholder={placeholder}
          value={value}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
        />
        <button
          type="button"
          data-testid={`${testid}-gps`}
          onClick={locate}
          title={lang === "en" ? "Use my current position" : "Utiliser ma position actuelle"}
          className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-azure transition-colors hover:bg-azure-soft"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Navigation size={16} />}
        </button>
      </div>

      {open && items.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-navy/10"
        >
          {items.map((item, i) => (
            <li key={item.key}>
              <button
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onClick={() => choose(item)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-azure-soft/60"
              >
                {iconFor(item.icon)}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-navy">{item.label}</span>
                  {item.sub && <span className="text-xs text-navy/45">{item.sub}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
